/**
 * The run's overlay layer: selection rim, range rings, the placement ghost, the
 * aim reticle, power-grid reach, and the flat HP bars over everything wounded.
 *
 * All of it is derived from state the run screen already has, so it lives here
 * rather than in the screen: one system, one file (doc 12 §Code health).
 */

import { Container, Sprite } from 'pixi.js';
import { blockCentre, TH, TW } from '../render/iso.ts';
import type { Camera } from '../render/camera.ts';
import { OUTLINE_HOVER, SEMANTIC } from '../render/palette.ts';
import { isoDiamondRim } from '../render/primitives.ts';
import { REGISTRY_DEFS } from '../art/registry.ts';
import { TextureCache } from '../render/pipeline.ts';
import { hex, PixelSurface } from '../render/surface.ts';
import { placeableDef, towerStats } from '../sim/defs.ts';
import type { Sim } from '../sim/sim.ts';
import type { AbilityId } from '../sim/types.ts';
import type { EffectsView } from './effects.ts';
import type { WorldView } from './worldview.ts';

export interface OverlayInputs {
  selectedStructId: number;
  tool: string | null;
  aiming: AbilityId | null;
  pointer: { x: number; y: number };
  /** Measuring Tape (doc 08): every tower keeps its ring, not just the selected one. */
  alwaysRanges: boolean;
  world: WorldView;
  fx: EffectsView;
  /** Reticle radius for the armed ability. */
  aimRadius: number;
  /** Tile under the pointer, in the screen's own coordinate maths. */
  pointerTile: (sx: number, sy: number) => { i: number; j: number };
}

export class OverlayView {
  private readonly sim: () => Sim;
  private readonly camera: Camera;
  private readonly cache: TextureCache;
  private readonly uiScale: () => number;
  private readonly padLayer: Container;
  private readonly barLayer: Container;

  private readonly gridRingPool: Sprite[] = [];
  private readonly barPool: Sprite[] = [];
  private readonly selectRim = new Sprite();
  private readonly rangeRing = new Sprite();
  private readonly ghostGround = new Sprite();
  readonly ghostBody = new Sprite();
  private readonly reticle = new Sprite();

  constructor(
    sim: () => Sim,
    camera: Camera,
    cache: TextureCache,
    uiScale: () => number,
    padLayer: Container,
    bodyLayer: Container,
    barLayer: Container,
  ) {
    this.sim = sim;
    this.camera = camera;
    this.cache = cache;
    this.uiScale = uiScale;
    this.padLayer = padLayer;
    this.barLayer = barLayer;
    this.selectRim.visible = false;
    this.rangeRing.visible = false;
    this.ghostGround.visible = false;
    this.ghostBody.visible = false;
    this.reticle.visible = false;
    padLayer.addChild(this.selectRim, this.rangeRing, this.ghostGround, this.reticle);
    bodyLayer.addChild(this.ghostBody);
    this.ghostBody.zIndex = 9990;
  }

  /** The run screen clears its own selection when the structure is gone. */
  lostSelection = false;


  private rimTexture(n: number, ok: boolean): Sprite['texture'] {
    return this.cache.get(`rim:${n}:${ok}`, () => {
      const s = new PixelSurface(TW * n * 2 + 4, TH * n * 2 + 4);
      isoDiamondRim(s, s.width >> 1, s.height >> 1, TH * n, ok ? SEMANTIC.good : SEMANTIC.critical);
      return s.resetOps();
    });
  }

  private rangeTexture(range: number, parity: number): Sprite['texture'] {
    return this.cache.get(`range:${range}:${parity}`, () => {
      const rx = range * 32;
      const ry = range * 16;
      const s = new PixelSurface(rx * 2 + 4, ry * 2 + 4);
      const cx = s.width >> 1;
      const cy = s.height >> 1;
      const steps = range * 40;
      for (let k = parity; k < steps; k += 2) {
        const t = (k / steps) * Math.PI * 2;
        s.px(cx + Math.round(rx * Math.cos(t)), cy + Math.round(ry * Math.sin(t)), OUTLINE_HOVER);
      }
      return s.resetOps();
    });
  }

  sync(o: OverlayInputs): void {
    const sel = o.selectedStructId >= 0 ? this.sim().byId(o.selectedStructId) : undefined;
    if (sel) {
      const c = blockCentre(sel.i, sel.j, sel.size, sel.size);
      this.selectRim.texture = this.rimTexture(sel.size, true);
      this.selectRim.x = c.x - (this.selectRim.texture.width >> 1);
      this.selectRim.y = c.y - (this.selectRim.texture.height >> 1);
      this.selectRim.visible = true;
      if (sel.kind === 'tower' && !sel.ruin) {
        const range = towerStats(sel.def, sel.upgrades).range;
        const parity = (this.sim().state.tick >> 4) & 1;
        this.rangeRing.texture = this.rangeTexture(range, parity);
        this.rangeRing.x = c.x - (this.rangeRing.texture.width >> 1);
        this.rangeRing.y = c.y - (this.rangeRing.texture.height >> 1);
        this.rangeRing.visible = true;
      } else {
        this.rangeRing.visible = false;
      }
    } else {
      this.selectRim.visible = false;
      this.rangeRing.visible = false;
      if (o.selectedStructId >= 0) this.lostSelection = true;
    }

    if (o.tool && o.pointer.x >= 0) {
      const t = o.pointerTile(o.pointer.x, o.pointer.y);
      const def = placeableDef(o.tool)!;
      const check = this.sim().grid.placeCheck(t.i, t.j, def.size, this.sim().state.structures, def.wants ?? null);
      const c = blockCentre(t.i, t.j, def.size, def.size);
      this.ghostGround.texture = this.rimTexture(def.size, check.ok);
      this.ghostGround.x = c.x - (this.ghostGround.texture.width >> 1);
      this.ghostGround.y = c.y - (this.ghostGround.texture.height >> 1);
      this.ghostGround.visible = true;
      const { r, key } = o.world.ghostRender(
        o.tool,
        o.tool === 'wall' ? o.world.wallMask(t.i, t.j) : 0,
      );
      this.ghostBody.texture = this.cache.get(`${key}#b`, () => r.body.surface);
      const registryGhost = REGISTRY_DEFS.has(o.tool);
      this.ghostBody.alpha = registryGhost ? 1 : 0.6;
      this.ghostBody.x = c.x - r.body.ax;
      this.ghostBody.y = c.y - r.body.ay;
      this.ghostBody.visible = true;
    } else {
      this.ghostGround.visible = false;
      this.ghostBody.visible = false;
    }

    if (o.aiming && o.pointer.x >= 0) {
      const w = this.camera.screenToWorld(o.pointer.x, o.pointer.y);
      this.reticle.texture = o.fx.reticleTexture(o.aimRadius, (this.sim().state.tick >> 3) & 1);
      this.reticle.x = Math.round(w.x) - (this.reticle.texture.width >> 1);
      this.reticle.y = Math.round(w.y) - (this.reticle.texture.height >> 1);
      this.reticle.visible = true;
    } else {
      this.reticle.visible = false;
    }

    // power-grid reach while placing powered things or with grid gear selected
    const showGrid =
      (o.tool !== null && (placeableDef(o.tool)!.power !== 0 || o.tool === 'power_pylon')) ||
      (sel !== undefined && ['power_pylon', 'generator', 'core_dome'].includes(sel.def));
    let rings = 0;
    if (showGrid) {
      const parity = (this.sim().state.tick >> 4) & 1;
      for (const st of this.sim().state.structures) {
        if (st.ruin) continue;
        const isEmitter = st.def === 'core_dome' || st.def === 'generator';
        const isRelay = st.def === 'power_pylon' && this.sim().connected.has(st.id);
        if (!isEmitter && !isRelay) continue;
        let sp = this.gridRingPool[rings];
        if (!sp) {
          sp = new Sprite();
          this.padLayer.addChild(sp);
          this.gridRingPool.push(sp);
        }
        sp.texture = this.rangeTexture(5, parity);
        const c = blockCentre(st.i, st.j, st.size, st.size);
        sp.x = c.x - (sp.texture.width >> 1);
        sp.y = c.y - (sp.texture.height >> 1);
        sp.visible = true;
        rings++;
      }
    }
    // Measuring Tape (doc 08): every tower keeps its range ring, always
    if (o.alwaysRanges) {
      const parity = (this.sim().state.tick >> 4) & 1;
      for (const st of this.sim().state.structures) {
        if (st.kind !== 'tower' || st.ruin || st.id === o.selectedStructId) continue;
        let sp = this.gridRingPool[rings];
        if (!sp) {
          sp = new Sprite();
          this.padLayer.addChild(sp);
          this.gridRingPool.push(sp);
        }
        sp.texture = this.rangeTexture(towerStats(st.def, st.upgrades).range, parity);
        const c = blockCentre(st.i, st.j, st.size, st.size);
        sp.x = c.x - (sp.texture.width >> 1);
        sp.y = c.y - (sp.texture.height >> 1);
        sp.alpha = 0.35;
        sp.visible = true;
        rings++;
      }
    }
    for (let k = rings; k < this.gridRingPool.length; k++) this.gridRingPool[k]!.visible = false;
  }

  private barTexture(level: number, tone: 'good' | 'critical'): Sprite['texture'] {
    return this.cache.get(`bar:${level}:${tone}`, () => {
      const s = new PixelSurface(12, 3, hex('#020709'));
      const col = tone === 'good' ? SEMANTIC.good : SEMANTIC.critical;
      if (level > 0) s.fillRect(1, 1, level, 1, col, 'set');
      return s.resetOps();
    });
  }

  bars(): void {
    let used = 0;
    const place = (wx: number, wy: number, frac: number, tone: 'good' | 'critical'): void => {
      let sp = this.barPool[used];
      if (!sp) {
        sp = new Sprite();
        this.barLayer.addChild(sp);
        this.barPool.push(sp);
      }
      sp.texture = this.barTexture(Math.max(1, Math.round(frac * 10)), tone);
      const s = this.camera.worldToScreen(wx, wy);
      sp.x = Math.round(s.x / this.uiScale()) - 6;
      sp.y = Math.round(s.y / this.uiScale());
      sp.visible = true;
      used++;
    };
    for (const e of this.sim().state.enemies) {
      if (e.hp >= e.maxHp) continue;
      place((e.x - e.y) * TW, (e.x + e.y) * TH - 6, e.hp / e.maxHp, 'critical');
    }
    for (const st of this.sim().state.structures) {
      if (st.ruin || st.hp >= st.maxHp) continue;
      const c = blockCentre(st.i, st.j, st.size, st.size);
      place(c.x, c.y - st.size * TH - 26, st.hp / st.maxHp, 'good');
    }
    for (let k = used; k < this.barPool.length; k++) this.barPool[k]!.visible = false;
  }
}
